// GYM-900 / GYM-479b / GYM-1048: Offline write queue using IndexedDB.
//
// Generic envelope — replaces the closed QueuedAction union.
// tokenAudience determines which auth store supplies the Bearer token at replay.
//
// PRIVACY: Only opaque IDs and action types are stored — no PHI, no member
// names, no medical data.  Notes are stored only as the POST body that the
// user already composed on their device.
//
// SW background sync tag: "gymviz-write-queue"

export type TokenAudience = "staff" | "member";

export interface QueuedAction {
  url: string;
  method: string;
  body: Record<string, unknown>;
  idempotencyKey: string;
  tokenAudience: TokenAudience;
}

export interface QueueEntry {
  id?: number;
  action: QueuedAction;
  enqueuedAt: string;
  attempts: number;
}

const DB_NAME = "gymviz-offline";
const STORE = "write-queue";
// v3: add workout-draft-sessions and workout-draft-sets stores (GYM-504).
const DB_VERSION = 3;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      const oldVersion = event.oldVersion;
      if (oldVersion < 1) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
      // v2: add workout-draft stores (also handled by workoutDraft.ts, but we
      // own the version bump so we must create them here for users upgrading
      // from v1 directly to v2+).
      if (oldVersion < 2) {
        if (!db.objectStoreNames.contains("workout-draft-sessions")) {
          db.createObjectStore("workout-draft-sessions", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("workout-draft-sets")) {
          const setStore = db.createObjectStore("workout-draft-sets", { keyPath: "id" });
          setStore.createIndex("sessionId", "sessionId");
        }
      }
      // v1→v2: migrate legacy trainer entries to the generic envelope shape.
      if (oldVersion >= 1 && oldVersion < 2) {
        const tx = (event.target as IDBOpenDBRequest).transaction!;
        const store = tx.objectStore(STORE);
        const getAllReq = store.getAll();
        getAllReq.onsuccess = () => {
          const entries = getAllReq.result as Array<{ id: number; action: Record<string, unknown>; enqueuedAt: string; attempts: number }>;
          for (const entry of entries) {
            const legacyAction = entry.action;
            if (legacyAction.type === "complete_booking") {
              const migrated: QueueEntry = {
                id: entry.id,
                action: {
                  url: `/api/v1/staff/pt/bookings/${legacyAction.bookingId}/complete/`,
                  method: "POST",
                  body: {
                    allow_insufficient_balance: false,
                    notes: legacyAction.note,
                    idempotency_key: legacyAction.idempotencyKey,
                  },
                  idempotencyKey: legacyAction.idempotencyKey as string,
                  tokenAudience: "staff",
                },
                enqueuedAt: entry.enqueuedAt,
                attempts: entry.attempts,
              };
              store.put(migrated);
            } else if (legacyAction.type === "checkin_booking") {
              const migrated: QueueEntry = {
                id: entry.id,
                action: {
                  url: `/api/v1/staff/pt/bookings/${legacyAction.bookingId}/check-in/`,
                  method: "POST",
                  body: { idempotency_key: legacyAction.idempotencyKey },
                  idempotencyKey: legacyAction.idempotencyKey as string,
                  tokenAudience: "staff",
                },
                enqueuedAt: entry.enqueuedAt,
                attempts: entry.attempts,
              };
              store.put(migrated);
            }
          }
        };
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueue(action: QueuedAction): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const entry: QueueEntry = {
      action,
      enqueuedAt: new Date().toISOString(),
      attempts: 0,
    };
    const tx = db.transaction(STORE, "readwrite");
    const req = tx.objectStore(STORE).add(entry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function dequeue(id: number): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const req = tx.objectStore(STORE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function listQueue(): Promise<QueueEntry[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result as QueueEntry[]);
    req.onerror = () => reject(req.error);
  });
}

export async function incrementAttempts(id: number): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const entry = getReq.result as QueueEntry;
      if (!entry) { resolve(); return; }
      entry.attempts += 1;
      const putReq = store.put(entry);
      putReq.onsuccess = () => resolve();
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}
